import { normalizeText } from './text.js';
import type { Category } from './model.js';

/** Regla de categorización (pestaña _Reglas). account vacío = todas las cuentas. */
export interface Rule {
  priority: number | null;
  account: string;
  contains: string;
  regex: string;
  amountEquals: number | null;
  direction: 'expense' | 'income' | '';
  category: string;
  active: boolean;
}

/** Gana la primera regla activa que coincide, por prioridad ascendente (sin prioridad = 999). '' si ninguna. */
export function matchRule(rules: Rule[], account: string, concept: string, amount: number): string {
  const up = normalizeText(concept);   // la regla y el concepto, igual: mayúsculas, sin acentos, espacios simples
  const active = rules.filter(r => r.active).filter(r => !r.account.trim() || r.account.trim() === account)
    .map((r, i) => ({ r, i }))
    .sort((a, b) => ((a.r.priority || 999) - (b.r.priority || 999)) || a.i - b.i)
    .map(x => x.r);
  for (const r of active) {
    if (r.direction === 'expense' && !(amount < 0)) continue;
    if (r.direction === 'income' && !(amount > 0)) continue;
    if (r.amountEquals !== null && Math.abs(amount - r.amountEquals) >= 0.001) continue;
    const contains = r.contains.trim(), regex = r.regex.trim();
    if (contains && !up.includes(normalizeText(contains))) continue;
    if (regex && !new RegExp(regex, 'i').test(String(concept ?? '').trim())) continue;
    if (!contains && !regex) continue;
    return r.category.trim();
  }
  return '';
}

export interface Categorized { category: string; isDefault: boolean }

/**
 * Palabras clave de Categorías: gana la palabra más larga contenida en el concepto.
 * Sin coincidencia: categoría por defecto según el signo (pendiente de revisar).
 */
export function keywordCategorize(categories: Category[], concept: string, amount: number,
  defaults = { income: 'Otros Ingresos', expense: 'Varios' }): Categorized {
  const t = normalizeText(concept);
  const kws = categories.flatMap(c => c.keywords.map(k => ({ keyword: normalizeText(k), category: c.name })))
    .filter(k => k.keyword)
    .map((k, i) => ({ ...k, i }))
    .sort((a, b) => (b.keyword.length - a.keyword.length) || a.i - b.i);
  const hit = kws.find(k => t.includes(k.keyword));
  if (hit) return { category: hit.category, isDefault: false };
  return { category: amount > 0 ? defaults.income : defaults.expense, isDefault: true };
}

/** Reglas primero; si ninguna coincide, palabras clave. */
export function categorize(rules: Rule[], categories: Category[], account: string, concept: string, amount: number): Categorized {
  const r = matchRule(rules, account, concept, amount);
  return r ? { category: r, isDefault: false } : keywordCategorize(categories, concept, amount);
}

/** Palabras de los extractos que no identifican al comercio (no sirven como palabra clave de una regla). */
const GENERIC = new Set(['PAGO', 'PAGOS', 'CON', 'TARJETA', 'TARJETAS', 'COMPRA', 'COMPRAS', 'ADEUDO', 'RECIBO', 'RECIBIDO', 'RECIBIDA', 'ENVIADO',
  'TRANSFERENCIA', 'REALIZADA', 'BIZUM', 'WWW', 'COM', 'HTTP', 'HTTPS', 'RESTAURANTES', 'CAFETERIAS', 'CONSULTE', 'CLIENTES', 'SANTA', 'MARIA',
  'MADRID', 'BARCELONA', 'ESPANA', 'SPAIN', 'CONCEPTO', 'SIN', 'DEL', 'LAS', 'LOS', 'POR', 'PARA', 'APPLE', 'PAY',
  'CENTRO', 'TIENDA', 'GRUPO', 'SERVICIO', 'SERVICIOS', 'SEGURO', 'SEGUROS']);

/**
 * Palabra clave propuesta para una regla: el nombre del comercio, nunca frases genéricas del banco
 * ("PAGO CON TARJETA") ni números de tarjeta o referencias (p. ej. "Www.amazon* qx4iq3hk5 - Pago con tarjeta" → AMAZON).
 */
export function suggestKeyword(concept: string): string {
  const words = normalizeText(concept).split(/[^A-Z0-9]+/).filter(Boolean);
  const good = words.filter(w => w.length >= 4 && /^[A-Z]+$/.test(w) && !GENERIC.has(w));
  return good[0] ?? '';
}
